import { useRef, useState } from "react";
import { UploadCloud, X, File } from "lucide-react";
import { useLanguage } from "../context/LanguageContext";

export default function DropZone({ accept, multiple, files, onChange, hint, hintSub }) {
  const { t } = useLanguage();
  const inputRef = useRef(null);
  const [dragging, setDragging] = useState(false);

  const handleFiles = (incoming) => {
    const arr = Array.from(incoming);
    if (multiple) {
      onChange([...files, ...arr]);
    } else {
      onChange([arr[0]]);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragging(false);
    if (e.dataTransfer.files.length) handleFiles(e.dataTransfer.files);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setDragging(true);
  };

  const handleDragLeave = () => setDragging(false);

  const removeFile = (idx) => {
    onChange(files.filter((_, i) => i !== idx));
  };

  const formatSize = (bytes) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1048576) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / 1048576).toFixed(1)} MB`;
  };

  return (
    <div className="space-y-4">
      <div
        onClick={() => inputRef.current?.click()}
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        data-testid="upload-dropzone"
        className={`
          relative cursor-pointer rounded-xl border-2 border-dashed p-10 text-center transition-all duration-200
          ${dragging
            ? "border-blue-500 bg-blue-50 scale-[1.01]"
            : "border-slate-300 bg-[#f5f5f5] hover:border-blue-400 hover:bg-blue-50/40"
          }
        `}
      >
        <input
          ref={inputRef}
          type="file"
          accept={accept}
          multiple={multiple}
          className="hidden"
          onChange={(e) => e.target.files && handleFiles(e.target.files)}
          data-testid="file-input"
        />
        <div className="flex flex-col items-center gap-3">
          <div className={`w-14 h-14 rounded-full flex items-center justify-center transition-colors ${dragging ? "bg-blue-100" : "bg-white shadow-sm"}`}>
            <UploadCloud className={`w-7 h-7 ${dragging ? "text-blue-600" : "text-slate-400"}`} />
          </div>
          <div>
            <p className="font-semibold text-slate-700" style={{ fontFamily: "'Outfit', sans-serif" }}>
              {hint}
            </p>
            <p className="text-sm text-slate-500 mt-1">{hintSub}</p>
          </div>
          <div className="flex items-center gap-2 text-sm text-slate-400">
            <span>—— {t.upload.or} ——</span>
          </div>
          <button
            type="button"
            className="px-5 py-2 rounded-lg bg-blue-600 text-white text-sm font-medium hover:bg-blue-700 active:scale-95 transition-all focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
            data-testid="browse-files-btn"
          >
            {t.upload.browse}
          </button>
          <p className="text-xs text-slate-400">{t.upload.maxSize}</p>
        </div>
      </div>

      {files.length > 0 && (
        <div className="space-y-2">
          {files.map((f, i) => (
            <div
              key={i}
              className="flex items-center justify-between px-4 py-3 bg-white rounded-lg border border-slate-200 shadow-sm"
              data-testid={`file-item-${i}`}
            >
              <div className="flex items-center gap-3 min-w-0">
                <div className="w-8 h-8 rounded-md bg-blue-50 flex items-center justify-center flex-shrink-0">
                  <File className="w-4 h-4 text-blue-600" />
                </div>
                <div className="min-w-0">
                  <p className="text-sm font-medium text-slate-800 truncate">{f.name}</p>
                  <p className="text-xs text-slate-400">{formatSize(f.size)}</p>
                </div>
              </div>
              <button
                onClick={(e) => { e.stopPropagation(); removeFile(i); }}
                className="ml-2 p-1.5 rounded-md hover:bg-red-50 text-slate-400 hover:text-red-500 transition-colors"
                data-testid={`remove-file-${i}`}
                aria-label="Remove file"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
